import { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import axios, { CancelTokenSource } from 'axios'
import {
  Typography,
  TYPOGRAPHY_WEIGHT
} from '../../../../components/Typography'
import { Button, BUTTON_TYPES } from '../../../../components/Button'
import { Input } from '../../../../components/Input'
import { UserArchivePopup } from '../../../../pages/CRUDUser/UserArchivePopup'
import { Table } from '../../../../components/Table'
import { ReactComponent as Search16 } from 'assets/svg/Search16.svg'
import { ReactComponent as Plus16 } from 'assets/svg/Plus16.svg'
import { ReactComponent as Close16 } from 'assets/svg/Clos16.svg'
import { ReactComponent as Edit16 } from 'assets/svg/Edit16.svg'
import { getUser } from '../../../../redux/store/user/getters'
import useRouter from '../../../../hooks/useRouter'
import { useDebounce } from '../../../../hooks/hooks'
import { setLoading } from '../../../../redux/store/common/slice'
import { getVendorsList } from './api'
import { getAllowedRolesList } from '../../../../redux/store/myCommunity/getters'
import {
  fullFormatPhoneNumber,
  isTableHasParams
} from '../../../../helper/common'
import { routes } from '../../../../router'
import { BTN_TXT, PLACEHOLDERS, USER_STATUSES } from '../../../../constants'
import { ability, ACTIONS, Can, SUBJECTS } from '../../../Permission'
import { DEFAULT_PAGE } from '../../../../components/Table/constants'
import { columns } from './constants'
import { filterColumns } from './utils'
import { onStartChat } from '../../utils'
import { MY_COMMUNITY_KEYS } from '../../constants'
import { ColumnsType } from 'antd/es/table'
import {
  VendorUser,
  VendorUsersFiltersFunc,
  VendorUsersFilters,
  VendorUsersRequestData,
  VendorUsersParams
} from './types'
import { UserRolesFilterComponent } from '../../../../components/UserRolesFilterComponent/UserRolesFilterComponent'
import './styles.scss'

export const VendorUsers = () => {
  const [activeTab, setActiveTab] = useState<string | null>(null)
  const [tableData, setTableData] = useState<VendorUser[]>([])
  const [selectedRowKeys, setSelectedRowKeys] = useState([])
  const [selectedRows, setSelectedRows] = useState<VendorUser[]>([])
  const [searchValue, setSearchValue] = useState('')
  const [sortParams, setSortParams] = useState<VendorUsersRequestData>({})
  const [filters, setFilters] = useState<VendorUsersFilters>({})
  const [tableColumns, setTableColumns] = useState<ColumnsType<VendorUser>>([])
  const [totalNumber, setTotalNumber] = useState(0)
  const [firstLoad, setFirstLoad] = useState(true)
  const [pageInfo, setPageInfo] = useState({ ...DEFAULT_PAGE })
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>()
  const tableDataTokenRef = useRef<CancelTokenSource>()
  const user = useSelector(getUser)
  const allowedRoles = useSelector(getAllowedRolesList)
  const dispatch = useDispatch()
  const { id } = useParams()
  const { push } = useRouter()
  const debouncedSearch = useDebounce(searchValue, 500)
  const vendorId = (user.vendor || id) as string

  const rolesOptions = useMemo(
    () => allowedRoles.find((i) => i.tab_name === 'vendor')?.roles || [],
    [allowedRoles]
  )

  useEffect(() => {
    return () => {
      if (!!tableDataTokenRef.current) {
        tableDataTokenRef.current.cancel()
      }
    }
  }, [])

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    timeoutRef.current = setTimeout(
      () => getTableData({ ...sortParams, ...filters }),
      firstLoad ? 0 : 500
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, activeTab])

  const getTableData = ({
    sortField,
    sortOrder,
    filters,
    page
  }: VendorUsersRequestData) => {
    dispatch(setLoading(true))
    if (!!tableDataTokenRef.current) {
      tableDataTokenRef.current.cancel()
    }
    const params: VendorUsersParams = { ...filters }
    if (searchValue) {
      params.search = searchValue
    }
    if (sortField) {
      if (sortOrder) {
        params.ordering = `${sortOrder === 'descend' ? '-' : ''}${sortField}`
      }
      setSortParams({
        sortField,
        sortOrder
      })
    }
    const dataPage = page ? page : pageInfo
    params.limit = dataPage.pageSize
    params.offset = (dataPage.pageNumber - 1) * dataPage.pageSize
    tableDataTokenRef.current = axios.CancelToken.source()
    getVendorsList(vendorId, activeTab, {
      cancelToken: tableDataTokenRef.current?.token,
      params
    })
      .then((resp) => {
        setTableColumns(
          filterColumns(
            activeTab ?? '',
            columns({
              applyFilters,
              filters,
              onStartChat,
              id: vendorId,
              role: activeTab ?? ''
            })
          )
        )
        if (!resp.data?.results) {
          return
        }
        setTableData(
          resp.data.results.map((i) => ({
            ...i,
            name: i.first_name,
            phone_number: fullFormatPhoneNumber(i.phone_number),
            key: i.uuid
          }))
        )
        setTotalNumber(resp.data.count)
        if (firstLoad) {
          setFirstLoad(false)
        }
      })
      .finally(() => dispatch(setLoading(false)))
  }

  const handleSelectRow = (selectedRowKeys, selectedRow) => {
    setSelectedRowKeys(selectedRowKeys)
    setSelectedRows(selectedRow)
  }

  const handleChangePageSize = (pageSize) => {
    const page = {
      pageNumber: 1,
      pageSize
    }
    getTableData({
      ...sortParams,
      filters,
      page
    })
    setPageInfo(page)
  }

  const createUser = () =>
    push(`${routes.createUserProfile}/new?fromCommunity=true`, {
      state: { redirectTab: MY_COMMUNITY_KEYS.vendors }
    })

  const handleRedirect = () =>
    push(
      `${routes.createUserProfile}/${selectedRowKeys[0]}?fromCommunity=true`,
      {
        state: { redirectTab: MY_COMMUNITY_KEYS.vendors }
      }
    )

  const handleTableChange = (pagination, _filters, sorter) => {
    const page = {
      pageNumber: pagination.current,
      pageSize: pagination.pageSize
    }
    getTableData({
      sortField: sorter.field,
      sortOrder: sorter.order,
      filters,
      page
    })
    setPageInfo(page)
  }

  const onChangeRole = (tab?: string) => {
    setSelectedRowKeys([])
    setSelectedRows([])
    setFilters({})
    setSortParams({})
    setSearchValue('')
    setActiveTab(tab ?? null)
    setTableColumns([])
  }

  const applyFilters: VendorUsersFiltersFunc = (field, appliedFilters) => {
    const newFilters = { ...filters, [field]: appliedFilters }
    setFilters(newFilters)
    getTableData({ ...sortParams, filters: newFilters })
  }

  return (
    <div className="vendor-users-list mt-32">
      <div className="vendor-users-list__header row align-center space-between">
        <div className="row gap-8">
          <UserRolesFilterComponent
            roles={rolesOptions}
            onChange={onChangeRole}
            groupRoles={false}
          />
          <div className="vendor-users-list__actions row align-center">
            {!!selectedRowKeys.length ? (
              <Typography.Body1 weight={TYPOGRAPHY_WEIGHT.SEMI_BOLD}>
                Selected {selectedRowKeys.length} of {totalNumber}
              </Typography.Body1>
            ) : (
              <Input
                className="vendor-users-list__search allow-clear"
                propsInput={{
                  allowClear: { clearIcon: <Close16 /> },
                  placeholder: PLACEHOLDERS.SEARCH_RFP_USERS,
                  prefix: <Search16 />,
                  value: searchValue,
                  onChange: (e) => setSearchValue(e.target.value)
                }}
              />
            )}
          </div>
        </div>
        <Can I={ACTIONS.CRUD} a={SUBJECTS.VENDOR_USERS}>
          <div className="row align-center gap-8">
            {!selectedRowKeys.length && (
              <Button
                type={BUTTON_TYPES.PRIMARY}
                icon={<Plus16 />}
                onClick={createUser}
                upperCase
              >
                {BTN_TXT.CREATE_USER}
              </Button>
            )}
            {selectedRowKeys.length === 1 && (
              <Button
                type={BUTTON_TYPES.PRIMARY}
                onClick={handleRedirect}
                icon={<Edit16 />}
                upperCase
              >
                {BTN_TXT.EDIT}
              </Button>
            )}
            {!!selectedRows.length &&
              selectedRows.filter((i) => i.status !== USER_STATUSES.ARCHIVED)
                .length === selectedRows.length && (
                <UserArchivePopup<VendorUser>
                  selected={selectedRows}
                  callback={() => {
                    setSelectedRowKeys([])
                    setSelectedRows([])
                    getTableData({ ...sortParams, ...filters })
                  }}
                  isTable
                />
              )}
          </div>
        </Can>
      </div>
      {!firstLoad && (
        <Table<VendorUser>
          dataSource={!tableColumns.length ? [] : tableData}
          columns={tableColumns}
          className="community-users-table mt-8"
          pageSize={pageInfo.pageSize}
          pagination={{
            pageSize: pageInfo.pageSize,
            current: pageInfo.pageNumber,
            total: totalNumber
          }}
          emptyText="No users found"
          onChangePage={handleChangePageSize}
          onChange={handleTableChange}
          hasSearchOrFilters={isTableHasParams<VendorUsersFilters>(
            debouncedSearch,
            filters
          )}
          createButton={
            ability.can(ACTIONS.CRUD, SUBJECTS.VENDOR_USERS)
              ? {
                  buttonText: BTN_TXT.CREATE_USER,
                  availability: true,
                  action: createUser
                }
              : undefined
          }
          rowSelection={
            ability.can(ACTIONS.CRUD, SUBJECTS.VENDOR_USERS)
              ? {
                  selectedRowKeys,
                  onChange: handleSelectRow,
                  getCheckboxProps: (record) => ({
                    disabled: record.uuid === user.uuid
                  })
                }
              : undefined
          }
        />
      )}
    </div>
  )
}
